import React from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import ContextualLink from '@magoosh/gre/app/contextual_link';
import style from './style.module.scss';
import Icon, { SectionIcons } from '@magoosh/gre/assets/icons';
import SegmentIllustration from '../../assets/top_non_strategy_segment.svg';
import { PremiumBadge } from '@magoosh/lib/info_badges';
import { SegmentDetailProps } from './types';
import { pluralize } from 'utilities/pluralize';
import { SegmentSlice } from '../../organisms/segment_slice';
import { paths } from 'config/path_helpers';
import { Wrapper } from '../../lib/wrapper';
import { PageCard } from '../../lib/page_card';
import { HeaderWithIllustration } from '../../lib/header_with_illustration';
import { useQueries } from '@magoosh/gre/app/queries';
import { useModalContext } from '@magoosh/context/modal_context';
import UpgradeForAccessMessage from '@magoosh/gre/adaptive_prep_v2/organisms/upgrade_for_access_message';

export const SegmentDetail: React.FC<SegmentDetailProps> = (props) => {
  const { segmentId } = useParams();
  const { setModal } = useModalContext();
  const query = useQueries().segmentDetailQuery(segmentId);

  const iconType: SectionIcons = {
    Math: 'Calc',
    Verbal: 'Notebook',
    Writing: 'Pen',
    GRE: 'Tutor'
  };

  const handleShowUpgradeModalClicked = () => {
    setModal({
      header: 'Upgrade now for access!',
      body: <UpgradeForAccessMessage handleClose={() => setModal(null)} />
    });
  };

  if (query.isLoading) {
    return null;
  } else {
    const { section, title, description, estimatedTimeMinutes, slices, locked } = query.data;

    return (
      <Wrapper>
        <Helmet>
          <title>{query.data.title} - Magoosh GRE</title>
        </Helmet>

        <div className="u-margin-V-m">
          <ContextualLink href={paths.dashboard()}>
            <strong>Home</strong>
          </ContextualLink>{' '}
          {'>'} <span>{title}</span>
        </div>
        <PageCard>
          <HeaderWithIllustration
            illustration={SegmentIllustration}
            illustrationAltText={'Segment Illustration'}
            icon={<Icon iconType={iconType[section]} fill={style.iconSecondary} />}>
            <h2 className="u-margin-A-n">
              <strong>{title}</strong>
            </h2>
            <p>
              <span>
                {locked && <PremiumBadge className={'u-margin-R-xs'} />}
                Estimated Time <strong>{pluralize(estimatedTimeMinutes, 'Minute')}</strong>
              </span>
            </p>
            <p>{description}</p>
          </HeaderWithIllustration>
          {slices.map((sliceProps, index) => {
            return (
              <SegmentSlice
                key={sliceProps.title}
                number={index + 1}
                {...sliceProps}
                segmentIsLocked={locked}
                showUpgradeModal={handleShowUpgradeModalClicked}
              />
            );
          })}
        </PageCard>
      </Wrapper>
    );
  }
};

export default SegmentDetail;
